import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  KanbanBoard,
  type KanbanColumn,
  type KanbanTask,
} from "@/components/ui/trello-kanban-board";
import { PipelineItemDrawer } from "@/components/pipeline-item-drawer";
import { isPipelineSocialPlatform } from "@shared/pipeline-social-platforms";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, RotateCcw, Search, X, Workflow } from "lucide-react";

const PIPELINE_COLUMN_IDS = [
  "pomysly",
  "do-napisania",
  "do-nagrania",
  "do-montazu",
  "gotowe-do-publikacji",
  "zaplanowane",
  "opublikowane",
] as const;

type PipelineColumnId = (typeof PIPELINE_COLUMN_IDS)[number];

const COLUMN_LABELS: Record<PipelineColumnId, string> = {
  pomysly: "Content ideas",
  "do-napisania": "Script / write",
  "do-nagrania": "Record",
  "do-montazu": "Edit",
  "gotowe-do-publikacji": "Ready to publish",
  zaplanowane: "Scheduled",
  opublikowane: "Published",
};

const COLUMN_TITLE_KEYS: Record<PipelineColumnId, string> = {
  pomysly: "contentPipeline.columns.pomysly",
  "do-napisania": "contentPipeline.columns.doNapisania",
  "do-nagrania": "contentPipeline.columns.doNagrania",
  "do-montazu": "contentPipeline.columns.doMontazu",
  "gotowe-do-publikacji": "contentPipeline.columns.gotoweDoPublikacji",
  zaplanowane: "contentPipeline.columns.zaplanowane",
  opublikowane: "contentPipeline.columns.opublikowane",
};

const COLUMN_COLORS: Record<string, string> = {
  pomysly: "bg-slate-500",
  "do-napisania": "bg-violet-500",
  "do-nagrania": "bg-blue-500",
  "do-montazu": "bg-indigo-500",
  "gotowe-do-publikacji": "bg-cyan-500",
  zaplanowane: "bg-amber-500",
  opublikowane: "bg-emerald-500",
};

const COLUMN_TO_PROCESS: Record<PipelineColumnId, string> = {
  pomysly: "pomysl",
  "do-napisania": "scenariusz",
  "do-nagrania": "nagranie",
  "do-montazu": "montaz",
  "gotowe-do-publikacji": "gotowe",
  zaplanowane: "zaplanowane",
  opublikowane: "opublikowane",
};

const PROCESS_TO_COLUMN = Object.fromEntries(
  Object.entries(COLUMN_TO_PROCESS).map(([columnId, process]) => [process, columnId])
) as Record<string, PipelineColumnId>;

const STORAGE_KEY = "content-pipeline-columns";
const CONTENT_TYPES_KEY = "content-pipeline-types";

const DEFAULT_CONTENT_TYPES = ["LONG FORMAT [HORIZONTAL]", "REELS [VERTICAL]", "POST", "KARUZELA"];

type ContentPipelineSnapshotResponse = {
  snapshot: {
    columns: KanbanColumn[];
    contentTypes: string[];
  } | null;
};

const buildEmptyColumns = (): KanbanColumn[] =>
  PIPELINE_COLUMN_IDS.map((id) => ({
    id,
    title: COLUMN_LABELS[id],
    tasks: [],
  }));

const dedupeStrings = (values: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const normalized = value.trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
  }

  return result;
};

const normalizeTask = (task: Partial<KanbanTask>, fallbackProcess?: string): KanbanTask => ({
  id: task.id ?? `task-${Date.now()}`,
  title: task.title?.trim() || "",
  description: task.description,
  labels: Array.isArray(task.labels) ? dedupeStrings(task.labels) : [],
  assignee: task.assignee,
  imageUrl: task.thumbnailUrl ?? task.imageUrl,
  createdAt: task.createdAt ?? new Date().toISOString(),
  process: task.process ?? fallbackProcess,
  editorUserId: task.editorUserId,
  editorName: task.editorName,
  contentTypes: Array.isArray(task.contentTypes) ? dedupeStrings(task.contentTypes) : [],
  scheduledAt: task.scheduledAt,
  fileUrl: task.fileUrl,
  thumbnailUrl: task.thumbnailUrl,
  script: task.script,
  approvalStatus: task.approvalStatus,
  approvedBy: task.approvedBy,
  approvedAt: task.approvedAt,
  rejectionReason: task.rejectionReason,
  scheduledPostId: task.scheduledPostId,
  scheduledPlatform: task.scheduledPlatform,
  publishedPostId: task.publishedPostId,
  publishedAt: task.publishedAt,
  socialPlatforms: Array.isArray(task.socialPlatforms)
    ? task.socialPlatforms.filter(isPipelineSocialPlatform)
    : undefined,
  notes: task.notes,
  referenceUrl: task.referenceUrl,
});

const REMOVED_COLUMN_IDS = ["nie-wyszlo", "evergreen"] as const;
const REMOVED_COLUMN_FALLBACK: PipelineColumnId = "gotowe-do-publikacji";

const normalizeColumns = (cols?: KanbanColumn[]): KanbanColumn[] => {
  const parsedColumns = Array.isArray(cols) ? cols : [];
  const existingMap = new Map(parsedColumns.map((column) => [column.id, column]));

  // Migrate tasks from removed columns into fallback column
  const migratedTasks: KanbanTask[] = REMOVED_COLUMN_IDS.flatMap((removedId) => {
    const removed = existingMap.get(removedId);
    return Array.isArray(removed?.tasks)
      ? removed.tasks.map((task) => normalizeTask(task, COLUMN_TO_PROCESS[REMOVED_COLUMN_FALLBACK]))
      : [];
  });

  return PIPELINE_COLUMN_IDS.map((id) => {
    const existing = existingMap.get(id);
    const fallbackProcess = COLUMN_TO_PROCESS[id];
    const baseTasks = Array.isArray(existing?.tasks)
      ? existing.tasks.map((task) => normalizeTask(task, fallbackProcess))
      : [];

    return {
      id,
      title: existing?.title || COLUMN_LABELS[id],
      tasks: id === REMOVED_COLUMN_FALLBACK ? [...baseTasks, ...migratedTasks] : baseTasks,
    };
  });
};

const getTaskContentTypes = (columns: KanbanColumn[]): string[] =>
  columns.flatMap((column) => column.tasks.flatMap((task) => task.contentTypes ?? []));

const mergeContentTypes = (types: string[], columns: KanbanColumn[]): string[] =>
  dedupeStrings([...DEFAULT_CONTENT_TYPES, ...types, ...getTaskContentTypes(columns)]);

const loadColumns = (): KanbanColumn[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return buildEmptyColumns();
    return normalizeColumns(JSON.parse(raw) as KanbanColumn[]);
  } catch {
    return buildEmptyColumns();
  }
};

const saveColumns = (cols: KanbanColumn[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cols));
};

const loadContentTypes = (columns: KanbanColumn[]): string[] => {
  try {
    const raw = localStorage.getItem(CONTENT_TYPES_KEY);
    const stored = raw ? (JSON.parse(raw) as string[]) : DEFAULT_CONTENT_TYPES;
    return mergeContentTypes(stored, columns);
  } catch {
    return mergeContentTypes(DEFAULT_CONTENT_TYPES, columns);
  }
};

const saveContentTypes = (types: string[]) => {
  localStorage.setItem(CONTENT_TYPES_KEY, JSON.stringify(types));
};

const getTaskMediaUrls = (task: KanbanTask): string[] =>
  [task.fileUrl, task.thumbnailUrl, task.imageUrl].filter((value): value is string =>
    Boolean(value)
  );

const applyMediaUrlRefreshToTask = (
  task: KanbanTask,
  refreshedUrls: Map<string, string>
): KanbanTask => {
  const nextFileUrl = task.fileUrl
    ? (refreshedUrls.get(task.fileUrl) ?? task.fileUrl)
    : task.fileUrl;
  const nextThumbnailUrl = task.thumbnailUrl
    ? (refreshedUrls.get(task.thumbnailUrl) ?? task.thumbnailUrl)
    : task.thumbnailUrl;
  const nextImageUrl = task.imageUrl
    ? (refreshedUrls.get(task.imageUrl) ?? task.imageUrl)
    : task.imageUrl;

  if (
    nextFileUrl === task.fileUrl &&
    nextThumbnailUrl === task.thumbnailUrl &&
    nextImageUrl === task.imageUrl
  ) {
    return task;
  }

  return {
    ...task,
    fileUrl: nextFileUrl,
    thumbnailUrl: nextThumbnailUrl,
    imageUrl: nextThumbnailUrl ?? nextImageUrl,
  };
};

const serializeSnapshot = (columns: KanbanColumn[], contentTypes: string[]): string =>
  JSON.stringify({ columns, contentTypes });

const hasSnapshotData = (columns: KanbanColumn[], contentTypes: string[]): boolean =>
  columns.some((column) => column.tasks.length > 0) ||
  contentTypes.some((type) => !DEFAULT_CONTENT_TYPES.includes(type));

export default function ContentPipeline() {
  const { t } = useTranslation();
  const initialColumns = normalizeColumns(loadColumns());
  const initialContentTypes = loadContentTypes(initialColumns);

  const [columns, setColumns] = useState<KanbanColumn[]>(initialColumns);
  const [contentTypes, setContentTypes] = useState<string[]>(initialContentTypes);
  const [selectedTask, setSelectedTask] = useState<KanbanTask | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isHydratedFromServer, setIsHydratedFromServer] = useState(false);

  // Filters — published hidden by default
  const [hidePublished, setHidePublished] = useState(true);
  const [search, setSearch] = useState("");
  const [labelFilter, setLabelFilter] = useState<string | null>(null);
  const [contentTypeFilter, setContentTypeFilter] = useState<string | null>(null);
  const [assigneeFilter, setAssigneeFilter] = useState<string | null>(null);
  const [approvalFilter, setApprovalFilter] = useState<
    "all" | "pending" | "approved" | "rejected" | "none"
  >("all");
  const [sortMode, setSortMode] = useState<"manual" | "newest" | "oldest">("manual");
  // Bulk-select mode for batch approval (Sprint 15)
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(() => new Set());
  // Empty-state help card can be dismissed permanently (persisted in localStorage).
  const [helpDismissed, setHelpDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("pipeline-help-dismissed") === "1";
    } catch {
      return false;
    }
  });
  const dismissHelp = useCallback(() => {
    setHelpDismissed(true);
    try {
      localStorage.setItem("pipeline-help-dismissed", "1");
    } catch {
      /* ignore */
    }
  }, []);
  const columnsRef = useRef(columns);
  const contentTypesRef = useRef(contentTypes);
  const lastPersistedSnapshotRef = useRef<string | null>(null);
  const pendingSaveTimeoutRef = useRef<number | null>(null);
  const saveFailureToastShownRef = useRef(false);
  const { toast } = useToast();

  const persistLocalSnapshot = useCallback(
    (nextColumns: KanbanColumn[], nextContentTypes: string[]) => {
      saveColumns(nextColumns);
      saveContentTypes(nextContentTypes);
    },
    []
  );

  // Undo history (Sprint 54) — bounded ring of last 10 snapshots.
  const undoStackRef = useRef<Array<{ cols: KanbanColumn[]; types: string[] }>>([]);
  const skipUndoCaptureRef = useRef(false);

  const applySnapshotLocally = useCallback(
    (nextColumns: KanbanColumn[], nextContentTypes: string[]) => {
      if (!skipUndoCaptureRef.current) {
        undoStackRef.current.push({ cols: columnsRef.current, types: contentTypesRef.current });
        if (undoStackRef.current.length > 10) undoStackRef.current.shift();
      }
      skipUndoCaptureRef.current = false;
      const normalizedColumns = normalizeColumns(nextColumns);
      const normalizedContentTypes = mergeContentTypes(nextContentTypes, normalizedColumns);
      setColumns(normalizedColumns);
      setContentTypes(normalizedContentTypes);
      persistLocalSnapshot(normalizedColumns, normalizedContentTypes);
      return { normalizedColumns, normalizedContentTypes };
    },
    [persistLocalSnapshot]
  );

  const undoLastChange = useCallback(() => {
    const previous = undoStackRef.current.pop();
    if (!previous) return false;
    skipUndoCaptureRef.current = true;
    const normalizedColumns = normalizeColumns(previous.cols);
    const normalizedContentTypes = mergeContentTypes(previous.types, normalizedColumns);
    setColumns(normalizedColumns);
    setContentTypes(normalizedContentTypes);
    persistLocalSnapshot(normalizedColumns, normalizedContentTypes);
    return true;
  }, [persistLocalSnapshot]);

  // Cmd/Ctrl+Z global handler when pipeline tab is focused
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
        const target = e.target as HTMLElement | null;
        if (
          target &&
          (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
        )
          return;
        if (undoStackRef.current.length === 0) return;
        e.preventDefault();
        undoLastChange();
      }
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [undoLastChange]);

  const persistSnapshotToServer = useCallback(
    async (
      nextColumns: KanbanColumn[],
      nextContentTypes: string[],
      options?: { silent?: boolean }
    ) => {
      const normalizedColumns = normalizeColumns(nextColumns);
      const normalizedContentTypes = mergeContentTypes(nextContentTypes, normalizedColumns);

      await apiRequest("PUT", "/api/content-pipeline", {
        columns: normalizedColumns,
        contentTypes: normalizedContentTypes,
      });

      lastPersistedSnapshotRef.current = serializeSnapshot(
        normalizedColumns,
        normalizedContentTypes
      );
      saveFailureToastShownRef.current = false;

      if (!options?.silent) {
        persistLocalSnapshot(normalizedColumns, normalizedContentTypes);
      }
    },
    [persistLocalSnapshot]
  );

  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  useEffect(() => {
    contentTypesRef.current = contentTypes;
  }, [contentTypes]);

  useEffect(() => {
    const handler = () => {
      const localColumns = normalizeColumns(loadColumns());
      const localContentTypes = loadContentTypes(localColumns);
      setColumns(localColumns);
      setContentTypes(localContentTypes);
    };

    window.addEventListener("content-pipeline-updated", handler);
    return () => window.removeEventListener("content-pipeline-updated", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const hydrateFromServer = async () => {
      const localColumns = normalizeColumns(loadColumns());
      const localContentTypes = loadContentTypes(localColumns);

      try {
        const response = (await apiRequest(
          "GET",
          "/api/content-pipeline"
        )) as ContentPipelineSnapshotResponse;
        if (cancelled) return;

        if (response.snapshot) {
          const serverColumns = normalizeColumns(response.snapshot.columns);
          const serverContentTypes = mergeContentTypes(
            response.snapshot.contentTypes ?? [],
            serverColumns
          );
          const { normalizedColumns, normalizedContentTypes } = applySnapshotLocally(
            serverColumns,
            serverContentTypes
          );
          lastPersistedSnapshotRef.current = serializeSnapshot(
            normalizedColumns,
            normalizedContentTypes
          );
        } else {
          const migrated = applySnapshotLocally(localColumns, localContentTypes);
          if (hasSnapshotData(migrated.normalizedColumns, migrated.normalizedContentTypes)) {
            await persistSnapshotToServer(
              migrated.normalizedColumns,
              migrated.normalizedContentTypes,
              { silent: true }
            );
          } else {
            lastPersistedSnapshotRef.current = serializeSnapshot(
              migrated.normalizedColumns,
              migrated.normalizedContentTypes
            );
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console -- jedyny slad bledu; apps/web nie ma loggera
        console.error("Failed to hydrate content pipeline from server:", error);
        applySnapshotLocally(localColumns, localContentTypes);
        toast({
          title: t("contentPipeline.offlineTitle"),
          description: t("contentPipeline.offlineDesc"),
        });
      } finally {
        if (!cancelled) {
          setIsHydratedFromServer(true);
        }
      }
    };

    void hydrateFromServer();

    return () => {
      cancelled = true;
    };
  }, [applySnapshotLocally, persistSnapshotToServer, toast, t]);

  useEffect(() => {
    if (!isHydratedFromServer) return;

    const serializedSnapshot = serializeSnapshot(columns, contentTypes);
    if (serializedSnapshot === lastPersistedSnapshotRef.current) return;

    if (pendingSaveTimeoutRef.current) {
      window.clearTimeout(pendingSaveTimeoutRef.current);
    }

    pendingSaveTimeoutRef.current = window.setTimeout(() => {
      void persistSnapshotToServer(columnsRef.current, contentTypesRef.current).catch((error) => {
        // eslint-disable-next-line no-console -- jedyny slad bledu; apps/web nie ma loggera
        console.error("Failed to persist content pipeline snapshot:", error);
        if (!saveFailureToastShownRef.current) {
          toast({
            title: t("contentPipeline.savedLocallyTitle"),
            description: t("contentPipeline.savedLocallyDesc"),
          });
          saveFailureToastShownRef.current = true;
        }
      });
    }, 400);

    return () => {
      if (pendingSaveTimeoutRef.current) {
        window.clearTimeout(pendingSaveTimeoutRef.current);
      }
    };
  }, [columns, contentTypes, isHydratedFromServer, persistSnapshotToServer, toast, t]);

  useEffect(
    () => () => {
      if (pendingSaveTimeoutRef.current) {
        window.clearTimeout(pendingSaveTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!isHydratedFromServer) return;

    let cancelled = false;

    const refreshPipelineMediaUrls = async () => {
      const urls = Array.from(
        new Set(columnsRef.current.flatMap((column) => column.tasks.flatMap(getTaskMediaUrls)))
      );
      if (urls.length === 0) return;

      try {
        const response = (await apiRequest("POST", "/api/storage/refresh-urls", { urls })) as {
          urls: string[];
        };
        if (cancelled || !Array.isArray(response.urls) || response.urls.length !== urls.length)
          return;

        const refreshedUrls = new Map<string, string>();
        response.urls.forEach((refreshedUrl, index) => {
          if (refreshedUrl && refreshedUrl !== urls[index]) {
            refreshedUrls.set(urls[index], refreshedUrl);
          }
        });

        if (refreshedUrls.size === 0) return;

        const nextColumns = columnsRef.current.map((column) => ({
          ...column,
          tasks: column.tasks.map((task) => applyMediaUrlRefreshToTask(task, refreshedUrls)),
        }));

        const { normalizedColumns: refreshedColumns, normalizedContentTypes: refreshedTypes } =
          applySnapshotLocally(nextColumns, contentTypesRef.current);
        lastPersistedSnapshotRef.current = serializeSnapshot(refreshedColumns, refreshedTypes);
        setSelectedTask((prev) => (prev ? applyMediaUrlRefreshToTask(prev, refreshedUrls) : prev));
      } catch {
        // Keep existing URLs until the next refresh cycle.
      }
    };

    void refreshPipelineMediaUrls();
    const interval = window.setInterval(() => {
      void refreshPipelineMediaUrls();
    }, 30 * 60_000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [applySnapshotLocally, isHydratedFromServer]);

  const handleColumnsChange = useCallback(
    (newCols: KanbanColumn[]) => {
      const withProcesses = newCols.map((col) => ({
        ...col,
        tasks: col.tasks.map((task) => ({
          ...task,
          process: COLUMN_TO_PROCESS[col.id as PipelineColumnId] ?? task.process,
        })),
      }));
      applySnapshotLocally(withProcesses, contentTypesRef.current);
    },
    [applySnapshotLocally]
  );

  const handleTaskMove = useCallback(
    (taskId: string, _fromColumnId: string, toColumnId: string) => {
      const newProcess = COLUMN_TO_PROCESS[toColumnId as PipelineColumnId];
      if (!newProcess) return;
      setSelectedTask((current) =>
        current?.id === taskId ? { ...current, process: newProcess } : current
      );
    },
    []
  );

  const handleReset = useCallback(() => {
    applySnapshotLocally(buildEmptyColumns(), DEFAULT_CONTENT_TYPES);
    setSelectedTask(null);
  }, [applySnapshotLocally]);

  const handleTaskClick = useCallback((task: KanbanTask) => {
    setSelectedTask(task);
    setDrawerOpen(true);
  }, []);

  const handleTaskDelete = useCallback(
    (taskId: string) => {
      const task = columnsRef.current.flatMap((c) => c.tasks).find((t) => t.id === taskId);
      const label = task?.title ? `„${task.title}"` : "to zadanie";
      if (!confirm(`Usunąć ${label} z pipeline? Cofniesz to przez Ctrl+Z.`)) return;
      const nextColumns = columnsRef.current.map((col) => ({
        ...col,
        tasks: col.tasks.filter((t) => t.id !== taskId),
      }));
      applySnapshotLocally(nextColumns, contentTypesRef.current);
      setSelectedTask((current) => (current?.id === taskId ? null : current));
      setDrawerOpen((current) => (selectedTask?.id === taskId ? false : current));
    },
    [applySnapshotLocally, selectedTask]
  );

  const handleSave = useCallback(
    (updated: KanbanTask) => {
      const normalizedTask: KanbanTask = normalizeTask(updated, updated.process);
      const currentColumns = columnsRef.current;
      const currentColumnId = currentColumns.find((column) =>
        column.tasks.some((task) => task.id === updated.id)
      )?.id;

      // Auto-move to "Gotowe do publikacji" when approval just flipped to 'approved'
      // and task isn't already in a published / ready column.
      const previousTask = currentColumns
        .flatMap((c) => c.tasks)
        .find((t) => t.id === normalizedTask.id);
      const justApproved =
        normalizedTask.approvalStatus === "approved" && previousTask?.approvalStatus !== "approved";
      const isInTerminalColumn =
        currentColumnId === "gotowe-do-publikacji" || currentColumnId === "opublikowane";
      const autoMoveTarget: PipelineColumnId | undefined =
        justApproved && !isInTerminalColumn ? "gotowe-do-publikacji" : undefined;
      if (autoMoveTarget) {
        normalizedTask.process = COLUMN_TO_PROCESS[autoMoveTarget];
      }

      const targetColumnId =
        autoMoveTarget ??
        (normalizedTask.process
          ? (PROCESS_TO_COLUMN[normalizedTask.process] ?? currentColumnId)
          : currentColumnId);

      let nextColumns = currentColumns.map((column) => ({
        ...column,
        tasks: column.tasks.filter((task) => task.id !== normalizedTask.id),
      }));

      if (targetColumnId) {
        nextColumns = nextColumns.map((column) =>
          column.id === targetColumnId
            ? { ...column, tasks: [...column.tasks, normalizedTask] }
            : column
        );
      } else {
        nextColumns = currentColumns.map((column) => ({
          ...column,
          tasks: column.tasks.map((task) =>
            task.id === normalizedTask.id ? normalizedTask : task
          ),
        }));
      }

      applySnapshotLocally(nextColumns, contentTypesRef.current);
      setSelectedTask(normalizedTask);
    },
    [applySnapshotLocally]
  );

  const handleAddContentType = useCallback(
    (tag: string) => {
      const nextContentTypes = mergeContentTypes(
        [...contentTypesRef.current, tag],
        columnsRef.current
      );
      applySnapshotLocally(columnsRef.current, nextContentTypes);
    },
    [applySnapshotLocally]
  );

  const handleRemoveContentType = useCallback(
    (tag: string) => {
      const nextContentTypes = mergeContentTypes(
        contentTypesRef.current.filter((type) => type !== tag),
        columnsRef.current
      );
      applySnapshotLocally(columnsRef.current, nextContentTypes);
    },
    [applySnapshotLocally]
  );

  const allLabels = useMemo(
    () =>
      Array.from(
        new Set(columns.flatMap((col) => col.tasks.flatMap((t) => t.labels ?? [])))
      ).filter(Boolean),
    [columns]
  );

  const allAssignees = useMemo(
    () =>
      Array.from(
        new Set(columns.flatMap((col) => col.tasks.map((t) => t.editorName ?? t.assignee ?? "")))
      ).filter(Boolean),
    [columns]
  );

  const filteredColumns = useMemo(() => {
    const q = search.trim().toLowerCase();
    return columns
      .filter((col) => !(hidePublished && col.id === "opublikowane"))
      .map((col) => {
        const filteredTasks = col.tasks.filter((task) => {
          if (
            q &&
            !task.title.toLowerCase().includes(q) &&
            !(task.description ?? "").toLowerCase().includes(q)
          )
            return false;
          if (labelFilter && !(task.labels ?? []).includes(labelFilter)) return false;
          if (contentTypeFilter && !(task.contentTypes ?? []).includes(contentTypeFilter))
            return false;
          if (
            assigneeFilter &&
            task.editorName !== assigneeFilter &&
            task.assignee !== assigneeFilter
          )
            return false;
          if (approvalFilter !== "all") {
            if (approvalFilter === "none" && task.approvalStatus) return false;
            if (approvalFilter !== "none" && task.approvalStatus !== approvalFilter) return false;
          }
          return true;
        });
        if (sortMode === "manual") return { ...col, tasks: filteredTasks };
        const sorted = [...filteredTasks].sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return sortMode === "newest" ? bTime - aTime : aTime - bTime;
        });
        return { ...col, tasks: sorted };
      });
  }, [
    columns,
    hidePublished,
    search,
    labelFilter,
    contentTypeFilter,
    assigneeFilter,
    approvalFilter,
    sortMode,
  ]);

  const approvalCounts = useMemo(() => {
    const tasks = columns.flatMap((c) => c.tasks);
    return {
      pending: tasks.filter((t) => t.approvalStatus === "pending").length,
      approved: tasks.filter((t) => t.approvalStatus === "approved").length,
      rejected: tasks.filter((t) => t.approvalStatus === "rejected").length,
      none: tasks.filter((t) => !t.approvalStatus).length,
    };
  }, [columns]);

  const hasActiveFilters =
    search || labelFilter || contentTypeFilter || assigneeFilter || approvalFilter !== "all";

  // J/K vim-style navigation through tasks (Sprint 56)
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key !== "j" && key !== "k" && key !== "enter") return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
      )
        return;
      const allVisible = filteredColumns.flatMap((c) => c.tasks);
      if (allVisible.length === 0) return;
      if (key === "enter" && selectedTask && !drawerOpen) {
        e.preventDefault();
        setDrawerOpen(true);
        return;
      }
      const currentIdx = selectedTask ? allVisible.findIndex((t) => t.id === selectedTask.id) : -1;
      let nextIdx = currentIdx;
      if (key === "j")
        nextIdx = currentIdx < 0 ? 0 : Math.min(allVisible.length - 1, currentIdx + 1);
      if (key === "k") nextIdx = currentIdx < 0 ? 0 : Math.max(0, currentIdx - 1);
      if (nextIdx !== currentIdx && nextIdx >= 0) {
        e.preventDefault();
        setSelectedTask(allVisible[nextIdx]);
      }
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [filteredColumns, selectedTask, drawerOpen]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 pt-3 pb-2 border-b border-white/[0.06] shrink-0 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-white font-semibold text-sm flex items-center gap-2">
            {t("contentPipeline.header")}
            {approvalCounts.pending > 0 && (
              <button
                onClick={() => setApprovalFilter("pending")}
                title="Pokaż tylko zadania do akceptacji"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border border-amber-500/40 bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-500/20 transition-colors"
              >
                ⏳ {approvalCounts.pending} do akceptacji
              </button>
            )}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => undoLastChange()}
              disabled={undoStackRef.current.length === 0}
              title="Cofnij ostatnią zmianę (Cmd/Ctrl+Z)"
              className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border border-white/[0.08] text-white/40 hover:text-white/70 hover:border-white/[0.15] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ↶ Cofnij
            </button>
            <button
              onClick={() => {
                setBulkSelectMode((v) => {
                  if (v) setBulkSelectedIds(new Set());
                  return !v;
                });
              }}
              className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border transition-colors ${
                bulkSelectMode
                  ? "border-[#ef3e29]/50 bg-[#ef3e29]/10 text-[#ef3e29]"
                  : "border-white/[0.08] text-white/40 hover:text-white/70 hover:border-white/[0.15]"
              }`}
            >
              {bulkSelectMode ? "✕ Wyjdź z zaznaczania" : "☑ Zaznacz wiele"}
            </button>
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 text-white/30 hover:text-white/60 text-xs transition-colors"
            >
              <RotateCcw size={12} />
              {t("contentPipeline.reset")}
            </button>
          </div>
        </div>
        {bulkSelectMode && (
          <div className="flex items-center flex-wrap gap-2 px-2 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08]">
            <span className="text-[11px] text-white/70 font-medium">
              Zaznaczono {bulkSelectedIds.size}
            </span>
            <button
              onClick={() => {
                const pendingIds = columns
                  .flatMap((c) => c.tasks)
                  .filter((t) => t.approvalStatus === "pending")
                  .map((t) => t.id);
                setBulkSelectedIds(new Set(pendingIds));
              }}
              disabled={approvalCounts.pending === 0}
              className="px-2 py-0.5 rounded-md text-[10px] font-medium border border-amber-500/30 text-amber-700 dark:text-amber-200/80 hover:bg-amber-100 dark:hover:bg-amber-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Zaznacz wszystkie do akceptacji ({approvalCounts.pending})
            </button>
            {filteredColumns.flatMap((c) => c.tasks).length > 0 && (
              <button
                onClick={() => {
                  const visibleIds = filteredColumns.flatMap((c) => c.tasks).map((t) => t.id);
                  setBulkSelectedIds(new Set(visibleIds));
                }}
                className="px-2 py-0.5 rounded-md text-[10px] font-medium border border-white/[0.08] text-white/55 hover:text-white hover:border-white/[0.15] transition-colors"
              >
                Zaznacz wszystkie widoczne ({filteredColumns.flatMap((c) => c.tasks).length})
              </button>
            )}
          </div>
        )}
        {bulkSelectMode && bulkSelectedIds.size > 0 && (
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-emerald-500/[0.06] border border-emerald-500/30">
            <span className="text-[11px] text-emerald-200 font-medium">
              Akcje ({bulkSelectedIds.size})
            </span>
            <button
              onClick={() => {
                const ids = Array.from(bulkSelectedIds);
                ids.forEach((id) => {
                  const task = columnsRef.current.flatMap((c) => c.tasks).find((t) => t.id === id);
                  if (task)
                    handleSave({
                      ...task,
                      approvalStatus: "approved",
                      approvedAt: new Date().toISOString(),
                    });
                });
                setBulkSelectedIds(new Set());
              }}
              className="px-2.5 py-0.5 rounded-md text-[10px] font-medium border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 transition-colors"
            >
              ✓ Zatwierdź zaznaczone ({bulkSelectedIds.size})
            </button>
            <button
              onClick={() => {
                const ids = Array.from(bulkSelectedIds);
                ids.forEach((id) => {
                  const task = columnsRef.current.flatMap((c) => c.tasks).find((t) => t.id === id);
                  if (task) handleSave({ ...task, approvalStatus: "rejected" });
                });
                setBulkSelectedIds(new Set());
              }}
              className="px-2.5 py-0.5 rounded-md text-[10px] font-medium border border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20 transition-colors"
            >
              ✕ Odrzuć zaznaczone
            </button>
            <button
              onClick={() => {
                if (
                  !confirm(
                    `Usunąć ${bulkSelectedIds.size} zaznaczone zadania? Ta akcja jest nieodwracalna.`
                  )
                )
                  return;
                const idsToDelete = new Set(bulkSelectedIds);
                const nextColumns = columnsRef.current.map((col) => ({
                  ...col,
                  tasks: col.tasks.filter((t) => !idsToDelete.has(t.id)),
                }));
                applySnapshotLocally(nextColumns, contentTypesRef.current);
                setBulkSelectedIds(new Set());
              }}
              className="px-2.5 py-0.5 rounded-md text-[10px] font-medium border border-red-700/50 bg-red-900/30 text-red-300 hover:bg-red-900/50 transition-colors"
            >
              🗑 Usuń zaznaczone
            </button>
            <button
              onClick={() => setBulkSelectedIds(new Set())}
              className="ml-auto text-[10px] text-white/40 hover:text-white/70 transition-colors"
            >
              Wyczyść zaznaczenie
            </button>
          </div>
        )}

        {/* Filter bar */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative">
            <Search
              size={11}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("contentPipeline.searchPlaceholder")}
              className="bg-white/[0.04] border border-white/[0.08] rounded-lg pl-6 pr-2 py-1 text-xs text-white placeholder:text-white/30 outline-none focus:border-white/20 w-36"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
              >
                <X size={10} />
              </button>
            )}
          </div>

          {/* Hide published toggle */}
          <button
            onClick={() => setHidePublished((v) => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
              hidePublished
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                : "bg-white/[0.04] border-white/[0.08] text-white/40 hover:text-white/60"
            }`}
          >
            {hidePublished ? <EyeOff size={11} /> : <Eye size={11} />}
            {t("contentPipeline.published")}
          </button>

          {/* Label filter */}
          {allLabels.length > 0 && (
            <select
              value={labelFilter ?? ""}
              onChange={(e) => setLabelFilter(e.target.value || null)}
              className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1 text-xs text-white/60 outline-none focus:border-white/20"
            >
              <option value="">{t("contentPipeline.allLabels")}</option>
              {allLabels.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          )}

          {/* Content type filter */}
          {contentTypes.length > 0 && (
            <select
              value={contentTypeFilter ?? ""}
              onChange={(e) => setContentTypeFilter(e.target.value || null)}
              className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1 text-xs text-white/60 outline-none focus:border-white/20"
            >
              <option value="">{t("contentPipeline.allTypes")}</option>
              {contentTypes.map((ct) => (
                <option key={ct} value={ct}>
                  {ct}
                </option>
              ))}
            </select>
          )}

          {/* Assignee filter */}
          {allAssignees.length > 0 && (
            <select
              value={assigneeFilter ?? ""}
              onChange={(e) => setAssigneeFilter(e.target.value || null)}
              className="bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-1 text-xs text-white/60 outline-none focus:border-white/20"
            >
              <option value="">{t("contentPipeline.allAssignees")}</option>
              {allAssignees.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          )}

          {/* Approval status filter */}
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-white/[0.08]">
            {(
              [
                { id: "all", label: "Wszystkie", cls: "border-white/[0.08] text-white/60" },
                {
                  id: "pending",
                  label: `⏳ Do akceptacji${approvalCounts.pending ? ` (${approvalCounts.pending})` : ""}`,
                  cls: "border-amber-500/40 bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200",
                },
                {
                  id: "approved",
                  label: `✓ Zatwierdzone${approvalCounts.approved ? ` (${approvalCounts.approved})` : ""}`,
                  cls: "border-emerald-500/40 text-emerald-200 bg-emerald-500/10",
                },
                {
                  id: "rejected",
                  label: `✕ Odrzucone${approvalCounts.rejected ? ` (${approvalCounts.rejected})` : ""}`,
                  cls: "border-red-500/40 text-red-200 bg-red-500/10",
                },
              ] as const
            ).map((opt) => {
              const isActive = approvalFilter === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setApprovalFilter(opt.id)}
                  className={`px-2 py-0.5 rounded-md border text-[10px] font-medium transition-all ${
                    isActive
                      ? opt.cls
                      : "border-white/[0.08] text-white/40 hover:text-white hover:border-white/[0.15]"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* Sort dropdown */}
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-white/[0.08]">
            <span className="text-[10px] text-white/40">Sortuj:</span>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as "manual" | "newest" | "oldest")}
              className="bg-white/[0.04] border border-white/[0.08] rounded-md px-1.5 py-0.5 text-[10px] text-white/70 outline-none focus:border-white/20"
            >
              <option value="manual">Ręcznie</option>
              <option value="newest">Najnowsze pierwsze</option>
              <option value="oldest">Najstarsze pierwsze</option>
            </select>
          </div>

          {/* Clear all filters */}
          {(hasActiveFilters || sortMode !== "manual") && (
            <button
              onClick={() => {
                setSearch("");
                setLabelFilter(null);
                setContentTypeFilter(null);
                setAssigneeFilter(null);
                setApprovalFilter("all");
                setSortMode("manual");
              }}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-xs transition-colors"
            >
              <X size={10} />
              {t("contentPipeline.clearFilters")}
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-hidden relative">
        <KanbanBoard
          columns={filteredColumns.map((col) => ({
            ...col,
            title: COLUMN_TITLE_KEYS[col.id as PipelineColumnId]
              ? t(COLUMN_TITLE_KEYS[col.id as PipelineColumnId])
              : col.title,
          }))}
          onColumnsChange={handleColumnsChange}
          onTaskMove={handleTaskMove}
          columnColors={COLUMN_COLORS}
          onTaskClick={bulkSelectMode ? undefined : handleTaskClick}
          onTaskDelete={bulkSelectMode ? undefined : handleTaskDelete}
          selectableColumnIds={
            bulkSelectMode ? (PIPELINE_COLUMN_IDS as unknown as string[]) : undefined
          }
          selectedTaskIds={bulkSelectMode ? bulkSelectedIds : undefined}
          onTaskSelect={
            bulkSelectMode
              ? (taskId: string) => {
                  setBulkSelectedIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(taskId)) next.delete(taskId);
                    else next.add(taskId);
                    return next;
                  });
                }
              : undefined
          }
        />
        {columns.every((c) => c.tasks.length === 0) && !hasActiveFilters && !helpDismissed && (
          <div className="pointer-events-none absolute inset-x-0 top-24 flex justify-center px-6">
            <div className="pointer-events-none relative bg-background/95 border border-white/[0.08] rounded-xl px-6 py-5 max-w-md text-center backdrop-blur space-y-2">
              <button
                type="button"
                onClick={dismissHelp}
                title="Ukryj wskazówkę"
                aria-label="Ukryj wskazówkę"
                className="pointer-events-auto absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md text-white/40 transition-colors hover:bg-white/[0.08] hover:text-white/70"
              >
                <X size={14} />
              </button>
              <div className="w-12 h-12 mx-auto rounded-2xl bg-gradient-to-br from-[#ef3e29]/20 to-[#d63520]/10 flex items-center justify-center">
                <Workflow className="w-6 h-6 text-[#ef3e29]" />
              </div>
              <p className="text-sm text-white font-semibold">Pipeline jest pusty</p>
              <p className="text-[11px] text-white/55 leading-relaxed">
                Pierwsze zadanie dodasz przyciskiem{" "}
                <span className="text-white/80">+ Dodaj kreację</span> w dowolnej kolumnie. Zadania
                możesz przeciągać między statusami, oznaczać do akceptacji i podpinać avatary z
                generatora.
              </p>
              <p className="text-[10px] text-white/35">
                Po pierwszej grafice z generatora zadania pojawią się tu automatycznie.
              </p>
            </div>
          </div>
        )}
        {hasActiveFilters &&
          filteredColumns.every((c) => c.tasks.length === 0) &&
          columns.some((c) => c.tasks.length > 0) && (
            <div className="pointer-events-none absolute inset-x-0 top-24 flex justify-center px-6">
              <div className="bg-background border border-amber-500/30 rounded-xl px-5 py-4 max-w-md text-center shadow-lg space-y-2 pointer-events-auto">
                <p className="text-sm text-amber-700 dark:text-amber-200 font-medium">
                  Brak wyników dla bieżących filtrów
                </p>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Pipeline zawiera zadania, ale żadne nie pasuje do aktywnych filtrów (
                  {columns.flatMap((c) => c.tasks).length} ukrytych).
                </p>
                <button
                  onClick={() => {
                    setSearch("");
                    setLabelFilter(null);
                    setContentTypeFilter(null);
                    setAssigneeFilter(null);
                    setApprovalFilter("all");
                  }}
                  className="px-3 py-1 rounded-md text-[11px] font-medium border border-amber-500/40 bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-100 hover:bg-amber-200 dark:hover:bg-amber-500/20 transition-colors"
                >
                  Wyczyść wszystkie filtry
                </button>
              </div>
            </div>
          )}
      </div>

      <PipelineItemDrawer
        task={selectedTask}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSave}
        onDelete={handleTaskDelete}
        onDuplicate={(source) => {
          // Clone task; reset approval/published metadata so the copy starts fresh.
          const clone: KanbanTask = normalizeTask(
            {
              ...source,
              id: `task-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              title: `${source.title} (kopia)`.slice(0, 100),
              approvalStatus: undefined,
              approvedAt: undefined,
              approvedBy: undefined,
              rejectionReason: undefined,
              createdAt: new Date().toISOString(),
            },
            source.process
          );
          // Insert into the same column as the source.
          const sourceColumnId = columnsRef.current.find((c) =>
            c.tasks.some((t) => t.id === source.id)
          )?.id;
          const nextColumns = columnsRef.current.map((col) =>
            col.id === sourceColumnId ? { ...col, tasks: [...col.tasks, clone] } : col
          );
          applySnapshotLocally(nextColumns, contentTypesRef.current);
        }}
        availableContentTypes={contentTypes}
        onAddContentType={handleAddContentType}
        onRemoveContentType={handleRemoveContentType}
      />
    </div>
  );
}
